import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import loadPayments, {
  LOAD_PAYMENTS_LIST
} from 'src/store/actions/paymentsActions';
import { AutomaticDebitCollectionStatus } from 'src/utils/payments';
import {
  selectPaymentsList,
  selectPaymentsListCount,
  selectPaymentQueryOptions
} from 'src/store/selectors/paymentSelectors';
import { useNavigate, useParams } from 'react-router-dom';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import AutomaticDebitCollectionList from './AutomaticDebitCollectionList';

const AutomaticDebitCollectionMenuList = [
  {
    value: AutomaticDebitCollectionStatus.PENDING,
    label: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.PENDING',
    description: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.PENDING'
  },
  {
    value: AutomaticDebitCollectionStatus.DEBITED,
    label: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.DEBITED',
    description: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.DEBITED'
  },
  {
    value: AutomaticDebitCollectionStatus.PROCESSED,
    label: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.PROCESSED',
    description: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.PROCESSED'
  },
  {
    value: AutomaticDebitCollectionStatus.CANCELLED,
    label: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.CANCELLED',
    description: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.CANCELLED'
  },
  {
    value: AutomaticDebitCollectionStatus.FAILED_TO_BE_DEBITED,
    label: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.FAILED_TO_BE_DEBITED',
    description: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.FAILED_TO_BE_DEBITED'
  },
  {
    value: AutomaticDebitCollectionStatus.FAILED_TO_BE_PROCESSED,
    label: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.FAILED_TO_BE_PROCESSED',
    description: 'ENUMS.AUTOMATIC_DEBIT.STATUS_TABS.FAILED_TO_BE_PROCESSED'
  }
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.oneOf(Object.values(AutomaticDebitCollectionStatus))
    .isRequired,
  value: PropTypes.oneOf(Object.values(AutomaticDebitCollectionStatus))
    .isRequired
};

const AutomaticDebitCollectionListTabs = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const [value, setValue] = useState(AutomaticDebitCollectionStatus.PENDING);
  const dispatch = useDispatch();
  const queryOpts = useSelector(selectPaymentQueryOptions);
  const { t } = useTranslation();
  const payments = useSelector(selectPaymentsList);
  const count = useSelector(selectPaymentsListCount);
  const loading = useSelector(isLoadingSelector([LOAD_PAYMENTS_LIST.action]));

  const handleChange = useCallback(
    (_, newValue) => {
      setValue(newValue);
      navigate(`/santander-reports/automatic-debit-collections/${newValue}`);
      dispatch(
        loadPayments({
          ...queryOpts,
          status: newValue,
          page: 0
        })
      );
    },
    [setValue, queryOpts, navigate, dispatch]
  );

  const handleChangePage = useCallback(
    (_, pageValue) => {
      const currentTab = AutomaticDebitCollectionMenuList.find(
        item => item.value === value
      );

      dispatch(
        loadPayments({
          ...queryOpts,
          page: pageValue,
          status: currentTab?.value || value
        })
      );
    },
    [dispatch, queryOpts, value]
  );

  useEffect(() => {
    if (!tab) {
      setValue(AutomaticDebitCollectionStatus.PENDING);
      navigate(
        `/santander-reports/automatic-debit-collections/${AutomaticDebitCollectionStatus.PENDING}`
      );
    }
    if (tab) {
      if (Object.values(AutomaticDebitCollectionStatus).includes(tab)) {
        setValue(tab);
      } else {
        navigate(`/santander-reports/automatic-debit-collections`);
      }
    }
  }, [tab, navigate]);

  useEffect(() => {
    dispatch(loadPayments({ status: value }));
  }, [dispatch, value]);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar
        variant="outlined"
        position="static"
        sx={{
          border: 'none',
          borderBottom: '1px solid rgba(43,45,66,0.1)',
          background: 'transparent',
          flexDirection: 'row'
        }}
      >
        <Tabs
          indicatorColor="primary"
          textColor="primary"
          value={value}
          onChange={handleChange}
        >
          {AutomaticDebitCollectionMenuList.map(item => (
            <Tab
              key={item.value}
              label={t(item.label)}
              value={item.value}
              description={t(item.description)}
            />
          ))}
        </Tabs>
      </AppBar>
      <TabPanel value={value} index={value}>
        <AutomaticDebitCollectionList
          payments={payments}
          count={count}
          page={queryOpts.page}
          handleChangePage={handleChangePage}
          loading={loading}
        />
      </TabPanel>
    </Box>
  );
};

export default AutomaticDebitCollectionListTabs;
